import React, { useEffect, useState } from 'react';
import { Container, Card, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { INDIAN_STATES } from '../utils/indianStates';
import { getCitiesByState } from '../utils/indianCities';
import AutocompleteSelect from '../components/AutocompleteSelect';
import { validateIndianMobile, validateIndianPincode } from '../utils/validation';
import './Profile.css';

const Profile = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street_address: '',
    landmark: '',
    city: '',
    state: 'Tamil Nadu',
    pincode: '',
    country: 'India',
    profile_image: '',
  });
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ phone: '', pincode: '' });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        profile_image: user.profile_image || '',
      }));
      fetchDefaultAddress();
    }
  }, [user]);

  const fetchDefaultAddress = async () => {
    try {
      const response = await api.get('/user/shipping-addresses/default');
      if (response.address) {
        const addr = response.address;
        setDefaultAddress(addr);
        setFormData(prev => ({
          ...prev,
          street_address: addr.street_address || '',
          landmark: addr.landmark || '',
          city: addr.city || '',
          state: addr.state || 'Tamil Nadu',
          pincode: addr.pincode || '',
          country: addr.country || 'India',
        }));
      }
    } catch (error) {
      console.error('Error fetching default address:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    let finalValue = value;
    if (name === 'phone') finalValue = value.replace(/\D/g, '').slice(0, 12);
    if (name === 'pincode') finalValue = value.replace(/\D/g, '').slice(0, 6);
    setFormData(prev => {
      const next = { ...prev, [name]: finalValue };
      if (name === 'state') next.city = '';
      return next;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, we'll just store the file name or URL
      // In production, you'd upload to Firebase Storage first
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          profile_image: reader.result, // Base64 or URL
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFieldErrors({ phone: '', pincode: '' });
    const phoneValidation = validateIndianMobile(formData.phone);
    if (!phoneValidation.valid) {
      setFieldErrors(prev => ({ ...prev, phone: phoneValidation.message }));
      toast.error(phoneValidation.message);
      return;
    }
    const hasAddress = formData.street_address || formData.city || formData.state;
    if (formData.pincode || hasAddress) {
      const pincodeValidation = validateIndianPincode(formData.pincode);
      if (!pincodeValidation.valid) {
        setFieldErrors(prev => ({ ...prev, pincode: pincodeValidation.message }));
        toast.error(pincodeValidation.message);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.put('/user/auth/profile', {
        name: formData.name,
        phone: formData.phone,
        profile_image: formData.profile_image,
      });

      // Update user in context
      login(localStorage.getItem('userToken'), response.user);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Update default shipping address if we have address fields
      if (formData.street_address || formData.city || formData.state || formData.pincode) {
        await api.put('/user/shipping-addresses/default', {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          street_address: formData.street_address,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country,
        });
      }

      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        profile_image: user.profile_image || '',
        ...(defaultAddress ? {
          street_address: defaultAddress.street_address || '',
          landmark: defaultAddress.landmark || '',
          city: defaultAddress.city || '',
          state: defaultAddress.state || 'Tamil Nadu',
          pincode: defaultAddress.pincode || '',
          country: defaultAddress.country || 'India',
        } : { state: 'Tamil Nadu' }),
      }));
    }
    setEditing(false);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.png';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('data:')) {
      return imagePath; // Base64 image
    }
    if (imagePath.startsWith('/')) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const baseUrl = API_BASE_URL.replace(/\/api$/, '');
      return `${baseUrl}${imagePath}`;
    }
    return imagePath;
  };

  if (!user) {
    return (
      <div className="profile-page">
        <Container>
          <div className="text-center py-5">
            <p>Please login to view your profile</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Container>
        <h1 className="profile-title">My Profile</h1>

        <Card className="profile-card">
          <Card.Body>
            <Row>
              <Col md={4} className="text-center mb-4 mb-md-0">
                <div className="profile-image-section">
                  <div className="profile-image-wrapper">
                    <img
                      src={getImageUrl(formData.profile_image)}
                      alt={formData.name || 'Profile'}
                      className="profile-image"
                      onError={(e) => {
                        e.target.src = '/placeholder.png';
                      }}
                    />
                    {editing && (
                      <div className="image-overlay">
                        <label htmlFor="profile-image-upload" className="image-upload-label">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          Change Photo
                        </label>
                        <input
                          type="file"
                          id="profile-image-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              <Col md={8}>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!editing}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="disabled-field"
                    />
                    <Form.Text className="text-muted">
                      Email cannot be changed
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!editing}
                      placeholder="10 digits"
                      maxLength={12}
                      className={fieldErrors.phone ? 'border-danger' : ''}
                    />
                    {fieldErrors.phone && (
                      <Form.Text className="text-danger d-block">{fieldErrors.phone}</Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 1 *</Form.Label>
                    <Form.Control
                      type="text"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleChange}
                      disabled={!editing}
                      placeholder="House/Flat No., Building Name, Street"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2 (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleChange}
                      disabled={!editing}
                      placeholder="Landmark, Apt 4B"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>State *</Form.Label>
                        <AutocompleteSelect
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          options={INDIAN_STATES}
                          placeholder="Type or select state..."
                          disabled={!editing}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City *</Form.Label>
                        <AutocompleteSelect
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          options={formData.state ? getCitiesByState(formData.state) : []}
                          placeholder={formData.state ? 'Type or select city...' : 'Select state first'}
                          disabled={!editing || !formData.state}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postal Code *</Form.Label>
                        <Form.Control
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          disabled={!editing}
                          placeholder="6 digits only"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          inputMode="numeric"
                          className={fieldErrors.pincode ? 'border-danger' : ''}
                        />
                        {fieldErrors.pincode && (
                          <Form.Text className="text-danger d-block">{fieldErrors.pincode}</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country *</Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          disabled={!editing}
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {editing ? (
                    <div className="profile-actions">
                      <Button
                        type="submit"
                        variant="primary"
                        className="save-btn"
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="cancel-btn"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      className="edit-btn"
                      onClick={() => setEditing(true)}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Form>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Profile;
